import React, { createRef, useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { PortalContextProvider } from '@amzn/storm-ui';
import { NavWrapper, SidenavPopoverPortal } from './components/styles';
import { MobileNavSheet } from './components/MobileSideNav/MobileNavSheet';
import {
  selectIsMobileSideNavOpen,
  selectViewType,
  toggleMobileSideNav,
  VIEW_TYPE,
} from 'src/app/slice';
import { RootState, store } from 'src/app/store';
import { ConnectedMobileSideNav } from './components/MobileSideNav/MobileSideNav';
import {
  selectLandingAppName,
  selectToolbarItems,
  ToolbarItemProps,
  updateActiveCategory as dispatchUpdateActiveCategory,
  updateLandingAppName,
  updateSelectedNavItem,
  selectBackupToolbarItems,
} from './store/slice';
import {
  useDomRefById,
  useEscalatedPortalZIndex,
  useNavLayout,
  useObserveUrlChange,
} from 'src/commons/hooks';
import {
  Components,
  CONSOLE_HEADER_SECTION_ID,
  WeblabedFeatures,
} from 'src/commons/constants';
import { shouldRenderMenu } from 'src/commons/utils/helpers';
import { markComponentReady } from 'src/commons/utils/logWithTakt';
import { SidenavBundleProvider } from 'src/providers/PantherLocalizationProvider';
import {
  initAjaxNavState,
  initInternalNavState,
  initInternalNavStateWithBackupToolbar,
  NavState,
  updateToolbarItemsWithBackup,
} from 'src/features/nav/store/slice';
import { getNavState, getNavbarInit, getCsrfToken } from 'src/commons/apis';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import { NavMetricPublisher } from 'src/commons/utils/metrics';
import { logger } from 'src/commons/utils/logger';
import {
  setBtasNavStatePromise,
  getOrMintShadowId,
  getShadowHeaders,
} from 'src/commons/utils/shadowContext';
import { runGlobalNavShadow } from 'src/commons/utils/runShadowDiff';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { ConsolidatedSideNav } from 'src/features/nav/components/ConsolidatedSideNav/ConsolidatedSideNav';

export const Nav: React.VFC<{
  viewType: VIEW_TYPE;
  isMobileNavOpen: boolean;
  toolbarItems?: Array<ToolbarItemProps>;
  backupToolbarItems?: Array<ToolbarItemProps>;
  navData?: NavState;
  updateActiveCategory: (...args: any[]) => any;
  toggleMobileSideNav: () => void;
  landingAppName: string;
  updateLandingAppName: (...args: any[]) => any;
  updateSelectedNavItem: (...args: any[]) => any;
}> = ({
  viewType,
  isMobileNavOpen,
  toolbarItems,
  backupToolbarItems,
  navData,
  updateActiveCategory,
  toggleMobileSideNav,
  landingAppName,
  updateLandingAppName,
  updateSelectedNavItem,
}) => {
  const chromeConfig = useChromeConfigContext();
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const { isSpoofer } = internalChromeConfig;
  const isShadowTreatment = useIsFeatureEnabled(
    WeblabedFeatures.MIGRATE_NAV_ACCOUNTSWITCHER_TO_TRIM_T2,
  );
  const isTrimNavMigrationEnabled =
    useIsFeatureEnabled(WeblabedFeatures.MIGRATE_NAV_ACCOUNTSWITCHER_TO_TRIM) ||
    isShadowTreatment;
  useObserveUrlChange();
  // Adding this back weblab check so that Internal Apps can be visible in internal Ads Console view (when overriding to C). This needs to be removed when internal apps are onboarded to federate

  useEffect(() => {
    // Ensure the ajax call would not be triggered in spoofing mode
    const hasAjaxData = !isSpoofer;
    if (navData) {
      if (hasAjaxData) {
        store.dispatch(initInternalNavStateWithBackupToolbar(navData));
      } else {
        store.dispatch(initInternalNavState(navData));
      }
    }

    // Load globalNav from navState (control) or navbar-init (treatment) and
    // dispatch it. Under treatment, csrfToken is null here (navbar-init does not
    // mint one) — the token is loaded separately below.
    const shadowOn = isShadowTreatment;
    if (shadowOn) {
      // Mint the page shadowId eagerly so both diff sites (Nav here, and the
      // lazy AccountDropdown) share one correlation id.
      getOrMintShadowId();
    }

    const shadowHeaders = shadowOn ? getShadowHeaders() : undefined;
    const loadNav = async () => {
      try {
        if (isTrimNavMigrationEnabled) {
          // Initiate navbar-init (user-facing) before the shadow-only
          // getNavState so under connection saturation the render request is
          // dequeued first. The stash is still set synchronously, before
          // runGlobalNavShadow reads it below.
          const trimPromise = getNavbarInit({ headers: shadowHeaders });
          if (shadowOn) {
            setBtasNavStatePromise(getNavState({ headers: shadowHeaders }));
          }
          const trim = await trimPromise;
          store.dispatch(initAjaxNavState(trim.globalNav));
          if (shadowOn) {
            void runGlobalNavShadow(trim);
          }
        } else {
          const { globalNav, csrfToken } = await getNavState();
          window.__AAC_FIT_CSRF_TOKEN__ = csrfToken;
          store.dispatch(initAjaxNavState(globalNav));
        }
        NavMetricPublisher.publishCounterMonitor(`ajaxNavStateFailure`, 0);
      } catch (e: any) {
        if (shadowOn) {
          void runGlobalNavShadow({ error: e });
        }
        store.dispatch(updateToolbarItemsWithBackup());
        NavMetricPublisher.publishCounterMonitor(`ajaxNavStateFailure`, 1);
        logger.error('failed to load nav data:', e);
      }
    };

    // Fired separately from loadNav so a slow/failed token fetch never blocks
    // the nav render.
    const loadCsrfToken = async () => {
      try {
        window.__AAC_FIT_CSRF_TOKEN__ = await getCsrfToken();
      } catch (e: any) {
        logger.error('failed to load CSRF token:', e);
      }
    };

    const loadAjaxData = () => {
      if (!hasAjaxData) return;
      loadNav();
      if (isTrimNavMigrationEnabled) {
        loadCsrfToken();
      }
    };

    loadAjaxData();
  }, []);

  const portalRef = useDomRefById('AACChromePortal');
  // When navData doesn't exist, it's the web component path
  // Use the portal ref inside the web component
  const globalNavPortalRef = !navData ? createRef<HTMLDivElement>() : undefined;
  // Escalated portal for desktop sidenav popovers (see SidenavPopoverPortal). No sidenav
  // popover exists today; this future-proofs so an explicitly-opened one would escape the
  // rail's overflow and paint above a StormUI FocusOverlay via useEscalatedPortalZIndex.
  const sidenavPopoverPortalRef = useRef<HTMLDivElement>(null);
  useEscalatedPortalZIndex(sidenavPopoverPortalRef);
  const navRef = useRef<HTMLDivElement | null>(null);
  const headerSectionRef = useRef<HTMLDivElement | null>(
    document.querySelector(CONSOLE_HEADER_SECTION_ID),
  );

  useEffect(function () {
    markComponentReady(Components.NAV);
  }, []);

  // resize nav on mount.  This scenario happens when the user changes
  // from focus mode to standard mode
  const nav = useCallback((nav) => {
    if (!headerSectionRef.current) {
      headerSectionRef.current = document.querySelector(
        CONSOLE_HEADER_SECTION_ID,
      );
    }
    if (nav) {
      navRef.current = nav;
      if (headerSectionRef.current) {
        const headerSectionRect =
          headerSectionRef.current.getBoundingClientRect();
        nav.style.top = `${headerSectionRect.height}px`;
      }
    }
  }, []);

  useNavLayout(navRef);

  if (!shouldRenderMenu(viewType, chromeConfig.pageLayoutType)) {
    return null;
  }

  const effectiveToolbarItems =
    toolbarItems && toolbarItems.length > 0 ? toolbarItems : backupToolbarItems;

  if (viewType === VIEW_TYPE.DESKTOP) {
    return (
      <>
        <SidenavPopoverPortal
          id="AACChromeGlobalNavPortal"
          ref={sidenavPopoverPortalRef}
        />
        <PortalContextProvider portalElementRef={sidenavPopoverPortalRef}>
          <SidenavBundleProvider>
            <NavWrapper
              id="AACChromeNav"
              onMouseLeave={() => updateActiveCategory('')}
              ref={nav}
            >
              {effectiveToolbarItems && effectiveToolbarItems.length > 0 ? (
                <ConsolidatedSideNav
                  toolbarItems={effectiveToolbarItems}
                  updateSelectedNavItem={updateSelectedNavItem}
                  {...(!isSpoofer
                    ? {
                        landingAppName: landingAppName,
                        updateLandingAppName: updateLandingAppName,
                      }
                    : {})}
                />
              ) : null}
            </NavWrapper>
          </SidenavBundleProvider>
        </PortalContextProvider>
      </>
    );
  }

  return (
    <>
      {!navData && (
        <div id="AACChromeGlobalNavPortal" ref={globalNavPortalRef}></div>
      )}
      <PortalContextProvider
        portalElementRef={navData ? portalRef : globalNavPortalRef}
      >
        <SidenavBundleProvider>
          <MobileNavSheet
            closeButton={{
              type: 'icon',
              label: 'close',
            }}
            onClose={toggleMobileSideNav}
            isOpen={isMobileNavOpen}
            withCloseButton={false}
            type="dark"
            side="start"
            hasInnerPadding={false}
          >
            <ConnectedMobileSideNav />
          </MobileNavSheet>
        </SidenavBundleProvider>
      </PortalContextProvider>
    </>
  );
};

const connector = connect(
  (state: RootState) => {
    return {
      viewType: selectViewType(state),
      isMobileNavOpen: selectIsMobileSideNavOpen(state),
      toolbarItems: selectToolbarItems(state),
      backupToolbarItems: selectBackupToolbarItems(state),
      landingAppName: selectLandingAppName(state),
    };
  },
  (dispatch) => {
    return {
      updateActiveCategory: dispatchUpdateActiveCategory,
      toggleMobileSideNav,
      updateLandingAppName: (category) =>
        dispatch(updateLandingAppName(category)),
      updateSelectedNavItem: (toolbarItemId: string, linkId: string) =>
        dispatch(
          updateSelectedNavItem({
            toolbarItemId,
            linkId,
          }),
        ),
    };
  },
);

export const ConnectedNav = connector(Nav);
